import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import type { RootState } from '@/store/store';
import { API_GPS_TRACKER_URL } from '@/utils';
import {
  ActiveDriversResponse,
  ActiveDriverResponse,
  ActiveDriver,
} from '../models';
import {
  sendMockGpsTrackerTrack,
  fetchDriverNameFromAPI,
} from './gpsTrackerMock.service';

const activeDriversService = createApi({
  reducerPath: 'activeDriversService',
  baseQuery: createBaseQueryWithReauth(API_GPS_TRACKER_URL),
  tagTypes: ['ActiveDrivers'] as const,
  endpoints: (builder) => ({
    getActiveDrivers: builder.query<
      ActiveDriversResponse,
      { offset: number; limit: number }
    >({
      queryFn: async (
        { offset, limit },
        queryApi,
        _extraOptions,
        baseQuery
      ) => {
        await sendMockGpsTrackerTrack(() => queryApi.getState() as RootState);

        const result = await baseQuery({
          url: '/drivers/active',
          params: {
            offset,
            limit,
          },
        } as FetchArgs);

        if (result.data) {
          const state = queryApi.getState() as RootState;
          const token = state.auth.auth.accessToken;
          const response = result.data as ActiveDriversResponse;

          const enrichedDrivers = await Promise.all(
            response.data.map(async (driver: ActiveDriver) => {
              const driverName = await fetchDriverNameFromAPI(
                driver.driverId,
                token
              );
              return {
                ...driver,
                firstName: driverName.firstName,
                lastName: driverName.lastName,
              };
            })
          );

          return {
            data: {
              ...response,
              data: enrichedDrivers,
            },
          } as { data: ActiveDriversResponse };
        }

        return result as
          | { data: ActiveDriversResponse }
          | { error: FetchBaseQueryError };
      },
      providesTags: ['ActiveDrivers'],
    }),
    getActiveDriver: builder.query<ActiveDriverResponse, string>({
      queryFn: async (driverId, queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/drivers/active/${driverId}`,
        } as FetchArgs);

        if (result.data) {
          const state = queryApi.getState() as RootState;
          const token = state.auth.auth.accessToken;
          const response = result.data as ActiveDriverResponse;

          const driverName = await fetchDriverNameFromAPI(
            response.data.driverId,
            token
          );

          return {
            data: {
              ...response,
              data: {
                ...response.data,
                firstName: driverName.firstName,
                lastName: driverName.lastName,
              },
            },
          } as { data: ActiveDriverResponse };
        }

        return result as
          | { data: ActiveDriverResponse }
          | { error: FetchBaseQueryError };
      },
      providesTags: ['ActiveDrivers'],
    }),
  }),
});

export default activeDriversService;
export const { useGetActiveDriversQuery, useGetActiveDriverQuery } =
  activeDriversService;
